// src/functions/auth_fethcers.js
import { authAxios, plainAuthAxios } from "./axiosInstance";
import { authStorage } from "./auth";

export const registerUser = async (username, email, password) => {
  try {
    const response = await authAxios.post("/register", {
      username,
      email,
      password,
    });
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error.message;
  }
};

export const loginUser = async (username, password) => {
  try {
    // Create form data exactly as FastAPI expects it
    const params = new URLSearchParams({
      // grant_type: 'password',  // OAuth2 requires this
      username: username,
      password: password,
    });

    const response = await authAxios.post("/token", params, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });

    const { access_token, refresh_token, token_type, expires_in } =
      response.data;

    // Store auth data
    const expiresAt = new Date(
      Date.now() + (expires_in || 1800) * 1000
    ).toISOString();
    authStorage.set({
      accessToken: access_token,
      refreshToken: refresh_token,
      tokenType: token_type,
      expiresAt,
    });

    return response.data;
  } catch (error) {
    console.error("Login error:", error.response?.data || error.message);
    throw error.response?.data || error;
  }
};

// Use plainAxios for refresh token requests to avoid circular dependencies
export const refreshAccessToken = async (refreshToken) => {
  try {
    console.log("Using direct axios call for token refresh");
    const response = await plainAuthAxios.post("/refresh", {
      current_token: refreshToken,
    });
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error.message;
  }
};

export const logoutUser = async () => {
  try {
    const refreshToken = authStorage.getRefreshToken();
    if (refreshToken) {
      await authAxios.post("/logout", { refresh_token: refreshToken });
    }
    authStorage.clear();
  } catch (error) {
    authStorage.clear();
    throw error.response ? error.response.data : error.message;
  }
};

export const getCurrentUser = async () => {
  try {
    const token = localStorage.getItem("access_token");
    const response = await authAxios.get("/me", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error.message;
  }
};

export const sendAccessCode = async (email) => {
  try {
    const response = await authAxios.post("/client/access-code", { email });
    // The response might just indicate success; no tokens are stored yet.
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error.message;
  }
};

export const verifyAccessCode = async (email, access_code) => {
  try {
    const response = await authAxios.post("/client/verify-access-code", {
      email,
      access_code,
    });

    const { access_token, refresh_token, token_type, expires_in } =
      response.data;
    const expiresAt = new Date(
      Date.now() + (expires_in || 1800) * 1000
    ).toISOString();

    authStorage.set({
      accessToken: access_token,
      refreshToken: refresh_token,
      tokenType: token_type,
      expiresAt,
    });

    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error.message;
  }
};

// Portal email + password sign-in. Stores the session token exactly like
// verifyAccessCode so isClient()/ClientRoute treat both methods identically.
export const loginWithPassword = async (email, password) => {
  try {
    const response = await authAxios.post("/client/login", {
      email,
      password,
    });

    const { access_token, refresh_token, token_type, expires_in } =
      response.data;
    const expiresAt = new Date(
      Date.now() + (expires_in || 1800) * 1000
    ).toISOString();

    authStorage.set({
      accessToken: access_token,
      refreshToken: refresh_token,
      tokenType: token_type,
      expiresAt,
    });

    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error.message;
  }
};

// Whether the signed-in portal client already has a password (authenticated).
export const getPasswordStatus = async () => {
  try {
    const response = await authAxios.get("/client/password-status");
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error.message;
  }
};

// Set or change the signed-in client's own password (authenticated).
export const setPassword = async (newPassword, currentPassword) => {
  try {
    const response = await authAxios.post("/client/set-password", {
      new_password: newPassword,
      current_password: currentPassword || null,
    });
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error.message;
  }
};

export const forgotPassword = async (email) => {
  try {
    const response = await authAxios.post("/client/forgot-password", { email });
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error.message;
  }
};

export const resetPassword = async (email, token, newPassword) => {
  try {
    const response = await authAxios.post("/client/reset-password", {
      email,
      token,
      new_password: newPassword,
    });
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error.message;
  }
};
